// src/components/ClientTimeline.jsx
import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Stack,
  Typography,
  Chip,
  TextField,
  Button,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import NotesRoundedIcon from "@mui/icons-material/NotesRounded";

import { apiFetch } from "../api/http"; // ✅ uses token & base URL

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

export default function ClientTimeline({ clientId }) {
  const theme = useTheme();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newNote, setNewNote] = useState("");

  const load = async () => {
    if (!clientId) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/clients/${clientId}/timeline`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Timeline load error", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  const handleAddNote = async () => {
    if (!newNote.trim() || !clientId) return;
    try {
      const res = await apiFetch(`/api/clients/${clientId}/timeline`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "note",
          title: "Note",
          body: newNote.trim(),
          created_by: "Agent", // later: current logged-in user
        }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setNewNote("");
      await load();
    } catch (e) {
      console.error(e);
      alert("Could not save note. You may need to log in again.");
    }
  };

  const typeChipColor = (type) => {
    switch ((type || "").toLowerCase()) {
      case "call":
        return "secondary";
      case "status_change":
        return "warning";
      case "email":
        return "info";
      case "task":
        return "success";
      default:
        return "default";
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={1.5}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{
              width: 32,
              height: 32,
              borderRadius: "999px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              color: theme.palette.primary.main,
            }}
          >
            <NotesRoundedIcon fontSize="small" />
          </Box>
          <Box>
            <Typography fontWeight={700}>Client timeline</Typography>
            <Typography variant="caption" color="text.secondary">
              Notes, calls, and stage changes in one place.
            </Typography>
          </Box>
        </Stack>
      </Stack>

      {/* New note input */}
      {clientId ? (
        <Stack direction="row" spacing={1} mb={2}>
          <TextField
            size="small"
            placeholder="Add a quick note about this client…"
            fullWidth
            multiline
            minRows={1}
            maxRows={4}
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={!newNote.trim()}
            sx={{ whiteSpace: "nowrap" }}
          >
            Save note
          </Button>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary" mb={2}>
          Select a client on the left to see their timeline.
        </Typography>
      )}

      {/* Timeline list */}
      <Box sx={{ flexGrow: 1 }}>
        {loading ? (
          <Typography variant="body2" color="text.secondary">
            Loading timeline…
          </Typography>
        ) : !clientId ? null : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No activity yet. Add your first note above.
          </Typography>
        ) : (
          <Box sx={{ maxHeight: 320, overflowY: "auto" }}>
            <Stack spacing={1.25}>
              {items.map((item) => (
                <Box
                  key={item.id}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                  }}
                >
                  {/* Dot */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      mt: 0.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: "50%",
                        bgcolor: theme.palette.primary.main,
                      }}
                    />
                  </Box>

                  {/* Content */}
                  <Box sx={{ flexGrow: 1 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      mb={0.25}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" fontWeight={600}>
                          {item.title}
                        </Typography>
                        <Chip
                          size="small"
                          label={item.type}
                          color={typeChipColor(item.type)}
                          variant="outlined"
                          sx={{ height: 20, fontSize: 11 }}
                        />
                      </Stack>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        {formatDateTime(item.created_at)}
                      </Typography>
                    </Stack>
                    {item.body && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ whiteSpace: "pre-wrap" }}
                      >
                        {item.body}
                      </Typography>
                    )}
                    {item.created_by && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        By {item.created_by}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Box>
    </Paper>
  );
}
